import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    AppBar,
    Toolbar,
    Tabs,
    Tab,
    Box,
    useTheme,
    useMediaQuery,
    IconButton,
    Menu,
    MenuItem,
    Typography
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HomeIcon from '@mui/icons-material/Home';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HistoryIcon from '@mui/icons-material/History';
import { usePWAStatus } from '../../hooks/usePWAStatus';
import BottomNavBar from './BottomNavBar';

const AdminNav = () => {
    const theme = useTheme();
    const customerKey = useSelector((state) => state.auth.activeUnit.CustomerKey); // Ensure customerKey is available
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorEl, setAnchorEl] = useState(null);
    const { isPWA } = usePWAStatus();

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleTabChange = (event, newValue) => {
        const currentPath = location.pathname;
        
        switch (newValue) {
            case 0: // Dashboard
                if (currentPath !== '/dashboard') {
                    navigate('/dashboard');
                }
                break;
            case 1: // Manage Caregivers
                const managePath = `/caregivers/${customerKey}`;
                if (currentPath !== managePath) {
                    navigate(managePath);
                }
                break;
            case 2: // Add Caregiver
                const addPath = `/caregivers/${customerKey}/add`;
                if (currentPath !== addPath) {
                    navigate(addPath);
                }
                break;
            case 3: // Recent Changes
                const changesPath = `/caregivers/${customerKey}/changes`;
                if (currentPath !== changesPath) {
                    navigate(changesPath);
                }
                break;
            default:
                if (currentPath !== '/dashboard') {
                    navigate('/dashboard');
                }
        }
    };

    // Determine active tab based on current path
    const getActiveTab = () => {
        const path = location.pathname;
        if (path === '/dashboard') return 0;
        if (path.includes(`/caregivers/${customerKey}/changes`)) return 3;
        if (path.includes(`/caregivers/${customerKey}/add`)) return 2;
        if (path.includes(`/caregivers/${customerKey}`)) return 1;
        return 0; // Default to dashboard
    };

    // If PWA mode, render bottom navigation instead of top navigation
    if (isPWA) {
        return <BottomNavBar />;
    }

    return (
        <AppBar position="static" color="primary" sx={{ maxWidth: '800px', margin: '0 auto', mb: 2, borderRadius: '4px' }}>
            <Toolbar variant="dense">
                {isMobile ? (
                    <React.Fragment>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            Admin Panel
                        </Typography>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={() => { 
                                if (location.pathname !== '/dashboard') {
                                    navigate('/dashboard'); 
                                }
                                handleMenuClose(); 
                            }}>
                                <HomeIcon sx={{ mr: 1 }} /> Status
                            </MenuItem>
                            <MenuItem onClick={() => { 
                                const managePath = `/caregivers/${customerKey}`;
                                if (location.pathname !== managePath) {
                                    navigate(managePath); 
                                }
                                handleMenuClose(); 
                            }}>
                                <PeopleAltIcon sx={{ mr: 1 }} /> Manage Caregivers
                            </MenuItem>
                            <MenuItem onClick={() => { 
                                const addPath = `/caregivers/${customerKey}/add`;
                                if (location.pathname !== addPath) {
                                    navigate(addPath); 
                                }
                                handleMenuClose(); 
                            }}>
                                <PersonAddIcon sx={{ mr: 1 }} /> Add Caregiver
                            </MenuItem>
                            <MenuItem onClick={() => { 
                                const changesPath = `/caregivers/${customerKey}/changes`;
                                if (location.pathname !== changesPath) {
                                    navigate(changesPath); 
                                }
                                handleMenuClose(); 
                            }}>
                                <HistoryIcon sx={{ mr: 1 }} /> Recent Changes
                            </MenuItem>
                        </Menu>
                    </React.Fragment>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Tabs
                            value={getActiveTab()}
                            onChange={handleTabChange}
                            indicatorColor="secondary"
                            textColor="inherit"
                            variant="fullWidth"
                        >
                            <Tab icon={<HomeIcon />} label="Status" />
                            <Tab icon={<PeopleAltIcon />} label="Manage Caregivers" />
                            <Tab icon={<PersonAddIcon />} label="Add Caregiver" />
                            <Tab icon={<HistoryIcon />} label="Recent Changes" />
                        </Tabs>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default AdminNav;
